import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Users, UserCheck, UserX, Edit, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import baristaService from '../../services/baristaService';

const BaristasTab = () => {
  const { t } = useTranslation();
  const [baristas, setBaristas] = useState([]);
  const [showAddBarista, setShowAddBarista] = useState(false);
  const [showEditBarista, setShowEditBarista] = useState(false);
  const [editingBarista, setEditingBarista] = useState(null);
  const [newBarista, setNewBarista] = useState({ firstName: '', lastName: '', email: '', password: '' });

  const loadBaristas = useCallback(async () => {
    const result = await baristaService.getShopBaristas();
    if (result.success) setBaristas(result.data); else toast.error(result.error || t('barista.failedToLoadBaristas'));
  }, [t]);

  useEffect(() => { loadBaristas(); }, [loadBaristas]);

  const handleAddBarista = async (e) => {
    e.preventDefault();
    const result = await baristaService.createBarista(newBarista);
    if (result.success) {
      setShowAddBarista(false);
      setNewBarista({ firstName: '', lastName: '', email: '', password: '' });
      loadBaristas();
      toast.success(t('barista.baristaAddedSuccessfully'));
    } else {
      toast.error(result.error || t('barista.failedToAddBarista'));
    }
  };

  const openEditBarista = (barista) => {
    setEditingBarista(barista);
    setNewBarista({ firstName: barista.firstName, lastName: barista.lastName, email: barista.email, password: '' });
    setShowEditBarista(true);
  };

  const handleEditBarista = async (e) => {
    e.preventDefault();
    if (!editingBarista) return;
    const result = await baristaService.updateBarista(editingBarista.id, newBarista);
    if (result.success) {
      setShowEditBarista(false);
      setEditingBarista(null);
      setNewBarista({ firstName: '', lastName: '', email: '', password: '' });
      loadBaristas();
      toast.success(t('barista.baristaUpdatedSuccessfully'));
    } else {
      toast.error(result.error || t('barista.failedToUpdateBarista'));
    }
  };

  const handleDeleteBarista = async (id) => {
    if (!window.confirm(t('dashboard.areYouSureDeleteBarista'))) return;
    const result = await baristaService.deleteBarista(id);
    if (result.success) { loadBaristas(); toast.success(t('barista.baristaDeletedSuccessfully')); } else { toast.error(result.error || t('barista.failedToDeleteBarista')); }
  };

  const handleToggleBaristaStatus = async (id) => {
    const result = await baristaService.toggleBaristaStatus(id);
    if (result.success) { toast.success(t('barista.baristaStatusUpdatedSuccessfully')); loadBaristas(); } else { toast.error(result.error || t('barista.failedToUpdateBarista')); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">{t('barista.managementTitle')}</h2>
        <button onClick={() => setShowAddBarista(true)} className="btn-primary">
          <Plus className="w-4 h-4" /> {t('barista.addBarista')}
        </button>
      </div>
      {showAddBarista && (
        <div className="card mb-6">
          <h3 className="text-xl font-semibold text-white mb-4">{t('barista.addNewBarista')}</h3>
            <form onSubmit={handleAddBarista} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="text" placeholder={t('barista.firstName')} value={newBarista.firstName} onChange={(e)=>setNewBarista({...newBarista, firstName:e.target.value})} className="input-field" required />
              <input type="text" placeholder={t('barista.lastName')} value={newBarista.lastName} onChange={(e)=>setNewBarista({...newBarista, lastName:e.target.value})} className="input-field" required />
              <input type="email" placeholder={t('barista.email')} value={newBarista.email} onChange={(e)=>setNewBarista({...newBarista, email:e.target.value})} className="input-field" required />
              <input type="password" placeholder={t('barista.password')} value={newBarista.password} onChange={(e)=>setNewBarista({...newBarista, password:e.target.value})} className="input-field" required />
              <div className="md:col-span-2 flex justify-end space-x-3">
                <button type="button" onClick={()=>{setShowAddBarista(false); setNewBarista({firstName:'',lastName:'',email:'',password:''});}} className="btn-secondary">{t('common.cancel')}</button>
                <button type="submit" className="btn-primary">{t('barista.addBarista')}</button>
              </div>
            </form>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {baristas && baristas.length > 0 ? baristas.map(b => (
          <div key={b.id} className="card">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">{b.firstName} {b.lastName}</h3>
                <p className="text-gray-300">{b.email}</p>
              </div>
              <div className="flex items-center space-x-2">{b.active ? <UserCheck className="w-5 h-5 text-green-500" /> : <UserX className="w-5 h-5 text-red-500" />}</div>
            </div>
            <div className="flex justify-between items-center">
              <button onClick={() => openEditBarista(b)} className="btn-secondary btn-small"><Edit className="w-4 h-4" />{t('common.edit')}</button>
              <div className="flex space-x-2">
                <button onClick={() => handleToggleBaristaStatus(b.id)} className={`btn-small ${b.active ? 'btn-danger' : 'btn-success'}`}>{b.active ? (<><UserX className="w-4 h-4" />{t('barista.deactivate')}</>) : (<><UserCheck className="w-4 h-4" />{t('barista.activate')}</>)}</button>
                <button onClick={() => handleDeleteBarista(b.id)} className="btn-danger"><Trash2 className="w-4 h-4" />{t('common.delete')}</button>
              </div>
            </div>
          </div>
        )) : (
          <div className="md:col-span-3 text-center py-12">
            <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">{t('barista.noBaristas')}</h3>
            <p className="text-gray-400 mb-4">{t('barista.noBaristasDescription')}</p>
            <button onClick={()=>setShowAddBarista(true)} className="btn-primary"><Plus className="w-4 h-4 mr-2" />{t('barista.addFirstBarista')}</button>
          </div>
        )}
      </div>
      {showEditBarista && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[#1f2937] rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-white">{t('barista.editBarista')}</h3>
              <button onClick={()=>{setShowEditBarista(false); setEditingBarista(null); setNewBarista({firstName:'',lastName:'',email:'',password:''});}} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleEditBarista} className="space-y-4">
              <input type="text" placeholder={t('barista.firstName')} value={newBarista.firstName} onChange={(e)=>setNewBarista({...newBarista, firstName:e.target.value})} className="input-field" required />
              <input type="text" placeholder={t('barista.lastName')} value={newBarista.lastName} onChange={(e)=>setNewBarista({...newBarista, lastName:e.target.value})} className="input-field" required />
              <input type="email" placeholder={t('barista.email')} value={newBarista.email} onChange={(e)=>setNewBarista({...newBarista, email:e.target.value})} className="input-field" required />
              <input type="password" placeholder={t('barista.newPassword')} value={newBarista.password} onChange={(e)=>setNewBarista({...newBarista, password:e.target.value})} className="input-field" />
              <div className="flex justify-end space-x-3">
                <button type="button" onClick={()=>{setShowEditBarista(false); setEditingBarista(null); setNewBarista({firstName:'',lastName:'',email:'',password:''});}} className="btn-secondary">{t('common.cancel')}</button>
                <button type="submit" className="btn-primary">{t('barista.updateBarista')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BaristasTab;
